import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, ClipboardList, Bot, FileText, Settings, LogOut, User, ChevronDown, Menu, ChevronLeft } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';

const Layout = ({ user }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Load sidebar preference from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('fynex_sidebar_collapsed');
    if (savedState !== null) {
      setSidebarCollapsed(JSON.parse(savedState));
    }
  }, []);

  // Save sidebar preference to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('fynex_sidebar_collapsed', JSON.stringify(newState));
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  // Get user display name or email
  const getUserName = () => {
    if (!user) return 'User';
    return user.email?.split('@')[0] || 'User';
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/overview' || path === '/') return 'Overview';
    if (path === '/tasks') return 'Tasks';
    if (path === '/ai-assistant') return 'AI Assistant';
    if (path === '/documents') return 'Document Library';
    if (path === '/settings') return 'Settings';
    return 'Dashboard';
  };

  return (
    <div className="flex h-screen bg-[#0a0e1a]">
      {/* Sidebar */}
      <div 
        className={`${sidebarCollapsed ? 'w-[70px]' : 'w-[250px]'} frosted-sidebar flex flex-col transition-all duration-500 ease-in-out border-r border-white/8`} 
        data-testid="sidebar"
        style={{
          borderTopRightRadius: '20px',
          borderBottomRightRadius: '20px'
        }}
      >
        {/* Logo & Toggle */}
        <div className={`${sidebarCollapsed ? 'p-5 pt-5' : 'p-8'} border-b border-white/8 flex items-center justify-between`}>
          {!sidebarCollapsed && (
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent" data-testid="logo">
              Fynex
            </h1>
          )}
          <button
            onClick={toggleSidebar}
            className={`${sidebarCollapsed ? 'w-12 h-12 mx-auto' : 'p-2'} rounded-xl hover:bg-white/10 transition-all duration-200 text-gray-400 hover:text-white flex items-center justify-center`}
            data-testid="sidebar-toggle"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <Menu size={24} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 ${sidebarCollapsed ? 'px-3 py-5' : 'p-4'} space-y-3`} data-testid="nav-menu">
          <NavLink
            to="/overview"
            className={({ isActive }) =>
              sidebarCollapsed 
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-overview"
            title={sidebarCollapsed ? "Overview" : ""}
          >
            <TrendingUp size={24} />
            {!sidebarCollapsed && <span className="font-medium">Overview</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Overview
              </div>
            )}
          </NavLink>

          <NavLink
            to="/tasks"
            className={({ isActive }) =>
              sidebarCollapsed 
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-tasks"
            title={sidebarCollapsed ? "Tasks" : ""}
          >
            <ClipboardList size={24} />
            {!sidebarCollapsed && <span className="font-medium">Tasks</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Tasks
              </div>
            )}
          </NavLink>

          <NavLink
            to="/ai-assistant"
            className={({ isActive }) =>
              sidebarCollapsed 
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-ai-assistant"
            title={sidebarCollapsed ? "AI Assistant" : ""}
          >
            <Bot size={24} />
            {!sidebarCollapsed && <span className="font-medium">AI Assistant</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                AI Assistant
              </div>
            )}
          </NavLink>

          <NavLink
            to="/documents"
            className={({ isActive }) =>
              sidebarCollapsed 
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-documents"
            title={sidebarCollapsed ? "Documents" : ""}
          >
            <FileText size={24} />
            {!sidebarCollapsed && <span className="font-medium">Documents</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Documents
              </div>
            )}
          </NavLink>

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              sidebarCollapsed 
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-settings"
            title={sidebarCollapsed ? "Settings" : ""}
          >
            <Settings size={24} />
            {!sidebarCollapsed && <span className="font-medium">Settings</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Settings
              </div>
            )}
          </NavLink>
        </nav>

        {/* User Section */}
        <div className={`${sidebarCollapsed ? 'p-3 pb-5' : 'p-4'} border-t border-white/10`}>
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center relative group hover:scale-105 transition-all duration-200 cursor-pointer">
                <User size={20} />
                <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                  {getUserName()}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-10 h-10 rounded-xl text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 flex items-center justify-center relative group"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut size={20} />
                <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                  Logout
                </div>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors" data-testid="user-section">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <User size={20} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{getUserName()}</div>
                <div className="text-xs text-gray-400">{user?.email}</div>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-red-400 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="h-16 bg-[#1a1f2e] border-b border-white/10 flex items-center justify-between px-8" data-testid="top-bar">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white lg:hidden"
              data-testid="mobile-sidebar-toggle"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu size={20} />
            </button>
            <h2 className="text-xl font-semibold" data-testid="page-title">{getPageTitle()}</h2>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="user-dropdown-btn"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <User size={16} />
              </div>
              <span className="text-sm font-medium">{getUserName()}</span>
              <ChevronDown size={16} className="text-gray-400" />
            </button>
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 glass-card p-2 z-50" data-testid="user-dropdown-menu">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-2 text-red-400"
                  data-testid="user-dropdown-logout"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="page-content">
          <Outlet />
          
          {/* Fubotics Footer */}
          <div className="text-center py-4 mt-8">
            <p className="text-xs text-white/30">
              © 2025 Fubotics
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
